import { useRef, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { MessageCircle, X, Send, Loader2, Sparkles, HelpCircle, ThumbsUp, ThumbsDown, RotateCcw, Activity } from "lucide-react";
import ReactMarkdown from 'react-markdown';
import { useStickyCart } from "@/contexts/StickyCartContext";
import { useChat } from "@/contexts/ChatContext";
import { cn } from "@/lib/utils";
import { ContactSupportDialog } from "./ContactSupportDialog";
import { TypingIndicator } from "./TypingIndicator";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useTracking } from "@/hooks/use-tracking";

export function GlobalChatbot() {
  const [query, setQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [suggestionsVisible, setSuggestionsVisible] = useState(() => {
    // Load preference from session storage
    const dismissed = sessionStorage.getItem('suggestionsDismissed');
    return dismissed !== 'true';
  });
  const [isDismissing, setIsDismissing] = useState(false);
  const [feedback, setFeedback] = useState<Record<number, 'up' | 'down'>>({});
  const [showSupportDialog, setShowSupportDialog] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [showPulse, setShowPulse] = useState(() => {
    // Show pulse for first-time visitors
    const hasVisited = localStorage.getItem('chatbotVisited');
    return !hasVisited;
  });
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const stored = localStorage.getItem('chatbotWidth');
    return stored ? parseInt(stored, 10) : 400;
  });
  const [isResizing, setIsResizing] = useState(false);
  const [sessionId, setSessionId] = useState(() => {
    const stored = sessionStorage.getItem('chatSessionId');
    if (stored) return stored;
    const newId = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('chatSessionId', newId);
    return newId;
  });
  const [filterContext, setFilterContext] = useState<any>(null);
  const chatScrollRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { isStickyCartVisible, isCartSidebarOpen } = useStickyCart();
  const { isOpen, setIsOpen, messages, setMessages, clearMessages, hasIntroMessage, productContext } = useChat();
  const { toast } = useToast();
  const { trackChatbotOpened, trackChatbotMessageSent, trackChatbotFeedback, trackSupportDialogOpened } = useTracking();

  useEffect(() => {
    const mql = window.matchMedia('(min-width: 1024px)');
    const onChange = () => setIsDesktop(mql.matches);
    mql.addEventListener('change', onChange);
    setIsDesktop(mql.matches);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Load persisted chat state on mount
  useEffect(() => {
    const persistedState = localStorage.getItem('chatbotOpen');
    if (persistedState !== null) {
      const shouldBeOpen = persistedState === 'true';
      if (shouldBeOpen !== isOpen) {
        setIsOpen(shouldBeOpen);
      }
    }
  }, []);

  // Persist chat state whenever it changes
  useEffect(() => {
    localStorage.setItem('chatbotOpen', String(isOpen));
  }, [isOpen]);

  // Persist sidebar width whenever it changes
  useEffect(() => {
    localStorage.setItem('chatbotWidth', String(sidebarWidth));
  }, [sidebarWidth]);

  // Handle resizing
  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const newWidth = window.innerWidth - e.clientX;
      const clampedWidth = Math.max(300, Math.min(600, newWidth));
      setSidebarWidth(clampedWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  // Escape key listener
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  const handleClose = () => {
    if (isDesktop) {
      setIsClosing(true);
      setTimeout(() => {
        setIsOpen(false);
        setIsClosing(false);
      }, 300); // Match animation duration
    } else {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    if (chatScrollRef.current) {
      chatScrollRef.current.scrollTo({
        top: chatScrollRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [messages]);

  const parseSuggestions = (content: string): { mainContent: string; suggestions: string[] } => {
    const parts = content.split('---SUGGESTIONS---');
    if (parts.length === 2) {
      const mainContent = parts[0].trim();
      const suggestionLines = parts[1].trim().split('\n').filter(line => line.trim());
      return { mainContent, suggestions: suggestionLines };
    }
    return { mainContent: content, suggestions: [] };
  };

  const streamChat = async (userMessage: string) => {
    setIsLoading(true);
    
    // Track message sent
    trackChatbotMessageSent(userMessage.length);
    
    const newMessages = [
      ...messages,
      { role: 'user' as const, content: userMessage, timestamp: Date.now() }
    ];
    
    setMessages(newMessages);
    setQuery('');

    try {
      const CHAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/general-chat`;
      
      // Retrieve filter context from sessionStorage
      const filterContextStr = sessionStorage.getItem('chatFilterContext');
      const filterContext = filterContextStr ? JSON.parse(filterContextStr) : null;

      const resp = await fetch(CHAT_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
        },
        body: JSON.stringify({
          messages: newMessages.map(m => ({ role: m.role, content: m.content })),
          productContext: filterContext || productContext,
        }),
      });

      if (!resp.ok) {
        if (resp.status === 429) {
          toast({
            title: "Rate Limit Exceeded",
            description: "Too many requests. Please try again in a moment.",
            variant: "destructive",
          });
          setMessages(prev => prev.slice(0, -1)); // Remove user message
          setIsLoading(false);
          return;
        }
        if (resp.status === 402) {
          toast({
            title: "AI Credits Exhausted",
            description: "Please add credits to continue using the chatbot.",
            variant: "destructive",
          });
          setMessages(prev => prev.slice(0, -1)); // Remove user message
          setIsLoading(false);
          return;
        }
        throw new Error('Failed to get response');
      }

      if (!resp.body) {
        throw new Error('No response body');
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let textBuffer = '';
      let streamDone = false;
      let assistantMessage = '';
      const messageTimestamp = Date.now();

      setMessages(prev => [...prev, { role: 'assistant', content: '', timestamp: messageTimestamp }]);

      while (!streamDone) {
        const { done, value } = await reader.read();
        if (done) break;
        
        textBuffer += decoder.decode(value, { stream: true });

        let newlineIndex: number;
        while ((newlineIndex = textBuffer.indexOf('\n')) !== -1) {
          let line = textBuffer.slice(0, newlineIndex);
          textBuffer = textBuffer.slice(newlineIndex + 1);

          if (line.endsWith('\r')) line = line.slice(0, -1);
          if (line.startsWith(':') || line.trim() === '') continue;
          if (!line.startsWith('data: ')) continue;

          const jsonStr = line.slice(6).trim();
          if (jsonStr === '[DONE]') {
            streamDone = true;
            break;
          }

          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content;
            if (content) {
              assistantMessage += content;
              const { mainContent, suggestions: extractedSuggestions } = parseSuggestions(assistantMessage);
              
              setMessages(prev => {
                const updated = [...prev];
                updated[updated.length - 1] = { role: 'assistant', content: mainContent, timestamp: messageTimestamp };
                return updated;
              });
              
              // Update suggestions when we detect them
              if (extractedSuggestions.length > 0) {
                setSuggestions(extractedSuggestions);
                // Only show if user hasn't dismissed in this session
                const dismissed = sessionStorage.getItem('suggestionsDismissed');
                if (dismissed !== 'true') {
                  setSuggestionsVisible(true);
                }
              }
            }
          } catch {
            textBuffer = line + '\n' + textBuffer;
            break;
          }
        }
      }

      // Parse final message to extract suggestions and main content
      const { mainContent: finalMainContent, suggestions: finalSuggestions } = parseSuggestions(assistantMessage);
      
      // Save conversation after streaming completes
      const finalMessages = [
        ...newMessages,
        { role: 'assistant' as const, content: finalMainContent, timestamp: messageTimestamp }
      ];
      
      // Update messages with final parsed content
      setMessages(finalMessages);
      
      // Set final suggestions
      if (finalSuggestions.length > 0) {
        setSuggestions(finalSuggestions);
        // Only show if user hasn't dismissed in this session
        const dismissed = sessionStorage.getItem('suggestionsDismissed');
        if (dismissed !== 'true') {
          setSuggestionsVisible(true);
        }
      }
      
      // Save to database in background
      saveConversation([
        { role: 'user' as const, content: userMessage, timestamp: newMessages[newMessages.length - 1].timestamp },
        { role: 'assistant' as const, content: assistantMessage, timestamp: messageTimestamp }
      ]);

    } catch (error) {
      console.error('Chat error:', error);
      toast({
        title: "Error",
        description: "Failed to get response. Please try again.",
        variant: "destructive",
      });
      setMessages(prev => prev.slice(0, -1)); // Remove the empty assistant message
    } finally {
      setIsLoading(false);
    }
  };

  const handleFeedback = async (messageTimestamp: number, type: 'up' | 'down') => {
    setFeedback(prev => ({ ...prev, [messageTimestamp]: type }));
    
    // Track feedback event
    trackChatbotFeedback(type);
    
    // Save feedback to database using timestamp
    const message = messages.find(m => m.timestamp === messageTimestamp && m.role === 'assistant');
    if (message) {
      try {
        const { data: logs } = await supabase
          .from('conversation_logs')
          .select('id')
          .eq('session_id', sessionId)
          .eq('message_content', message.content)
          .eq('message_role', 'assistant')
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (logs) {
          await supabase
            .from('conversation_logs')
            .update({ feedback: type })
            .eq('id', logs.id);
        }
      } catch (error) {
        console.error('Error saving feedback:', error);
      }
    }
  };

  // Save conversation after streaming completes
  const saveConversation = async (newMessages: Array<{ role: string; content: string; timestamp: number }>) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const authHeader = session?.access_token ? `Bearer ${session.access_token}` : undefined;

      await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/save-conversation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authHeader && { 'Authorization': authHeader }),
        },
        body: JSON.stringify({
          sessionId,
          messages: newMessages,
        }),
      });
    } catch (error) {
      console.error('Error saving conversation:', error);
    }
  };

  const handleDismissSuggestions = () => {
    setIsDismissing(true);
    sessionStorage.setItem('suggestionsDismissed', 'true');
    // Wait for animation to complete before hiding
    setTimeout(() => {
      setSuggestionsVisible(false);
      setIsDismissing(false);
    }, 300); // Match the fade-out animation duration
  };

  const handleToggleSuggestions = (enabled: boolean) => {
    if (enabled) {
      sessionStorage.removeItem('suggestionsDismissed');
      setSuggestionsVisible(true);
    } else {
      handleDismissSuggestions();
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    streamChat(suggestion);
    setSuggestions([]);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim() || isLoading) return;
    setSuggestions([]);
    streamChat(query);
  };

  // Auto-submit query when opened from AISearchBox or predefined questions
  useEffect(() => {
    if (isOpen) {
      // Load filter context
      const filterContextStr = sessionStorage.getItem('chatFilterContext');
      if (filterContextStr) {
        try {
          setFilterContext(JSON.parse(filterContextStr));
        } catch (e) {
          console.error('Error parsing filter context:', e);
        }
      }
      
      // Check if there's a pending query to submit
      const pendingQuery = sessionStorage.getItem('pendingChatQuery');
      if (pendingQuery) {
        sessionStorage.removeItem('pendingChatQuery');
        
        // Clear intro message when auto-submitting a query
        if (hasIntroMessage) {
          setMessages([]);
        }
        
        streamChat(pendingQuery);
      }
    } else {
      // Clear suggestions when chat closes
      setSuggestions([]);
      setFilterContext(null);
    }
  }, [isOpen]);

  const handleCompareProducts = () => {
    if (!filterContext?.visibleProducts || filterContext.visibleProducts.length < 2) return;
    
    const productNames = filterContext.visibleProducts
      .slice(0, 5)
      .map((p: { name: string }) => p.name)
      .join('", "');
    
    const comparisonQuery = `Please compare these products side-by-side: "${productNames}". Show me the key differences in ingredients, dosage, pricing, and which one might be best for different needs.`;
    
    // Clear intro message if present
    if (hasIntroMessage) {
      setMessages([]);
    }
    
    streamChat(comparisonQuery);
  };

  return (
    <>
      {/* Chat Button - Bottom-left as original */}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              onClick={() => {
                if (isOpen) {
                  handleClose();
                } else {
                  setIsOpen(true);
                  trackChatbotOpened('fab_button');
                  // Mark as visited and remove pulse
                  if (showPulse) {
                    localStorage.setItem('chatbotVisited', 'true');
                    setShowPulse(false);
                  }
                }
              }}
              size="icon"
              className={cn(
                "fixed left-6 z-[60] h-14 w-14 rounded-full shadow-lg transition-all duration-300 bg-primary hover:bg-primary/90",
                isStickyCartVisible ? "bottom-[calc(5.5rem+3px)]" : "bottom-6",
                isCartSidebarOpen ? "opacity-0 pointer-events-none scale-75" : "opacity-100 scale-100"
              )}
              aria-label={isOpen ? "Close chat" : "Open chat"}
            >
              {isOpen ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
              {/* Small notification dot for first-time visitors */}
              {showPulse && !isOpen && (
                <span className="absolute top-1 right-1 h-2.5 w-2.5 rounded-full bg-destructive border-2 border-background" />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right" className="bg-foreground text-background">
            <p>{isOpen ? "Close Assistant" : "Ask AI Assistant"}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {/* Chat Window - Desktop: right sidebar, Mobile/Tablet: left-side floating card */}
      {isOpen && (
        <Card 
          className={cn(
            "fixed z-[60] shadow-2xl flex flex-col bg-background border-border overflow-hidden",
            isResizing ? "transition-none" : "transition-all duration-500 ease-in-out",
            isDesktop
              ? cn(
                  "right-0 rounded-none border-l top-0 h-screen",
                  isClosing ? "animate-slide-out-right" : "animate-slide-in-right"
                )
              : cn(
                  "left-6 w-96 animate-scale-in rounded-lg h-[500px]",
                  isStickyCartVisible ? "bottom-44" : "bottom-24"
                )
          )}
          style={isDesktop ? { width: `${sidebarWidth}px` } : undefined}
        >
          {/* Drag Handle - Desktop only */}
          {isDesktop && (
            <div
              className="absolute left-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-primary/50 transition-colors group"
              onMouseDown={() => setIsResizing(true)}
            >
              <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-12 bg-primary/30 group-hover:bg-primary/70 transition-colors" />
            </div>
          )}

          <div className={cn(
            "p-4 border-b bg-primary text-primary-foreground flex items-center justify-between",
            !isDesktop && "rounded-t-lg"
          )}>
            <div className="flex-1">
              <h3 className="font-semibold">GetTested AI Assistant</h3>
              <p className="text-xs opacity-90">Ask me anything about supplements</p>
            </div>
            <div className="flex items-center gap-2 ml-auto">
              {!hasIntroMessage && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    clearMessages();
                    setSuggestions([]);
                    setFeedback({});
                    // Clear filter context when clearing chat
                    sessionStorage.removeItem('chatFilterContext');
                  }}
                  className="h-8 text-primary-foreground hover:bg-primary-foreground/20"
                  title="Clear chat"
                >
                  <RotateCcw className="h-4 w-4" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClose}
                className="h-8 text-primary-foreground hover:bg-primary-foreground/20"
                title="Close chat"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
          
          <ScrollArea className="flex-1 p-4">
            <div className="space-y-4">
              {/* Quick Action: Compare Products */}
              {filterContext?.visibleProducts && 
               filterContext.visibleProducts.length >= 2 && 
               filterContext.visibleProducts.length <= 5 && 
               messages.length <= 1 && (
                <Card className="bg-gradient-to-r from-accent/30 to-primary/10 border-primary/30">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="flex-shrink-0 bg-primary/10 rounded-full p-2">
                        <Activity className="w-5 h-5 text-primary" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-foreground mb-1">
                          Compare These Products
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Get a side-by-side comparison of the {filterContext.visibleProducts.length} products you're viewing
                        </p>
                      </div>
                      <Button
                        onClick={handleCompareProducts}
                        size="sm"
                        disabled={isLoading}
                        className="flex-shrink-0 gap-1.5"
                      >
                        <Activity className="w-4 h-4" />
                        Compare
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {messages.map((message, idx) => (
                <div key={idx}>
                  <div
                    className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-lg p-3 ${
                        message.role === 'user'
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-card border border-border text-foreground'
                      }`}
                    >
                      <div className="prose prose-sm dark:prose-invert max-w-none">
                        <ReactMarkdown>{message.content}</ReactMarkdown>
                      </div>
                      
                      {/* Feedback buttons for assistant messages */}
                      {message.role === 'assistant' && (
                        <div className="flex gap-1 mt-2 pt-2 border-t border-border/50">
                          <Button
                            variant="ghost"
                            size="sm"
                            className={cn(
                              "h-6 w-6 p-0",
                              feedback[message.timestamp] === 'up' && "text-green-600 hover:text-green-600"
                            )}
                            onClick={() => handleFeedback(message.timestamp, 'up')}
                          >
                            <ThumbsUp className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className={cn(
                              "h-6 w-6 p-0",
                              feedback[message.timestamp] === 'down' && "text-red-600 hover:text-red-600"
                            )}
                            onClick={() => handleFeedback(message.timestamp, 'down')}
                          >
                            <ThumbsDown className="h-3 w-3" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Show suggestions after the last assistant message */}
                  {message.role === 'assistant' && idx === messages.length - 1 && suggestions.length > 0 && !isLoading && suggestionsVisible && (
                    <div className={cn(
                      "mt-3 space-y-2",
                      isDismissing && "animate-fade-out"
                    )}>
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Sparkles className="h-3 w-3" />
                          <span>You might also want to know:</span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-5 w-5 p-0 hover:bg-muted"
                          onClick={handleDismissSuggestions}
                          title="Dismiss suggestions"
                          disabled={isDismissing}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                      {suggestions.map((suggestion, sIdx) => (
                        <Button
                          key={sIdx}
                          variant="outline"
                          size="sm"
                          className="w-full text-left justify-start h-auto py-2 px-3 text-xs"
                          onClick={() => handleSuggestionClick(suggestion)}
                          disabled={isDismissing}
                        >
                          {suggestion}
                        </Button>
                       ))}
                     </div>
                   )}
                   
                  {/* Not satisfied support button */}
                  {message.role === 'assistant' && idx === messages.length - 1 && !isLoading && (
                    <div className="mt-4 pt-3 border-t border-border/50 text-center">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          trackSupportDialogOpened('chatbot');
                          setShowSupportDialog(true);
                        }}
                        className="text-xs text-muted-foreground hover:text-foreground transition-colors h-auto py-1"
                      >
                        <HelpCircle className="h-3 w-3 mr-1" />
                        Not satisfied? Contact Support
                      </Button>
                    </div>
                  )}
                </div>
              ))}
              {isLoading && <TypingIndicator />}
              <div ref={messagesEndRef} />
            </div>
          </ScrollArea>
          
          <form onSubmit={handleSubmit} className="p-4 border-t">
            <div className="flex gap-2">
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Type your question..."
                disabled={isLoading}
                className="flex-1"
              />
              <Button type="submit" size="icon" disabled={isLoading || !query.trim()}>
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </form>
        </Card>
      )}
      
      <ContactSupportDialog
        open={showSupportDialog}
        onOpenChange={setShowSupportDialog}
        chatHistory={messages}
      />
    </>
  );
}
